import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported, deleteToken } from 'firebase/messaging';
import { isDevelopment } from 'src/module/helpers';


export const getNotificationPermission = () => {
    if (typeof Notification === "undefined") return "unsupported";
    return Notification.permission;
};


const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};



export const isWebView = /wv|Android/i.test(navigator.userAgent);

class FirebaseService {
    constructor() {
        this.app = null;
        this.messaging = null;
        this.isInitialized = false;
        this.vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
        this.serviceWorkerPath = '/firebase-messaging-sw.js';

        // State
        this.currentToken = null;
        this.permission = 'default';
        this.listeners = new Set();
    }

    /**
     * Initialize Firebase dengan error handling
     */
    async initialize() {
        if (this.isInitialized) return true;

        if (isWebView) {
            console.log('[FIREBASE] Skip init (WebView pakai native)');
            return false;
        }

        try {
            // Cek apakah browser support
            const supported = await isSupported();

            if (!supported) {
                console.warn('[FIREBASE] Browser tidak mendukung Firebase Messaging');
                return false;
            }

            // Initialize app
            this.app = initializeApp(firebaseConfig);
            this.messaging = getMessaging(this.app);

            this.isInitialized = true;
            console.log('[FIREBASE] Initialized successfully');

            // Check permission status
            this.permission = getNotificationPermission();

            return true;

        } catch (error) {
            console.error('[FIREBASE] Initialization failed:', error);
            return false;
        }
    }

    /**
     * Register service worker dengan retry mechanism
     */
    // async registerServiceWorker() {
    //     if (!('serviceWorker' in navigator)) {
    //         throw new Error('Service Worker tidak didukung');
    //     }

    //     try {
    //         // Cek service worker sudah terdaftar
    //         const existingRegistration = await navigator.serviceWorker.getRegistration(this.serviceWorkerPath);
    //         if (existingRegistration) {
    //             console.log('[FIREBASE] Service Worker sudah terdaftar');
    //             return existingRegistration;
    //         }

    //         // Register service worker baru
    //         const registration = await navigator.serviceWorker.register(this.serviceWorkerPath, {
    //             scope: '/',
    //             updateViaCache: 'none'
    //         });

    //         console.log('[FIREBASE] Service Worker registered:', registration);

    //         // Wait for service worker to be ready
    //         await navigator.serviceWorker.ready;

    //         return registration;

    //     } catch (error) {
    //         console.error('[FIREBASE] Service Worker registration failed:', error);
    //         throw error;
    //     }
    // }
    async registerServiceWorker() {
        if (!('serviceWorker' in navigator)) {
            throw new Error('Service Worker tidak didukung');
        }

        try {
            let registration = await navigator.serviceWorker.getRegistration(this.serviceWorkerPath);

            if (!registration) {
                registration = await navigator.serviceWorker.register(this.serviceWorkerPath, {
                    scope: '/',
                    updateViaCache: 'none'
                });
                if (isDevelopment)
                    console.log('[FIREBASE] Service Worker registered:', registration);
            } else {
                if (isDevelopment)
                    console.log('[FIREBASE] Service Worker sudah ada');
            }

            // 🔥 WAJIB: tunggu sampai READY & ACTIVE
            const readyRegistration = await navigator.serviceWorker.ready;

            // 🔥 EXTRA SAFETY (optional tapi recommended)
            if (!readyRegistration.active) {
                await new Promise((resolve) => {
                    const check = setInterval(() => {
                        if (readyRegistration.active) {
                            clearInterval(check);
                            resolve();
                        }
                    }, 100);
                });
            }

            return readyRegistration;

        } catch (error) {
            console.error('[FIREBASE] Service Worker registration failed:', error);
            throw error;
        }
    }

    /**
     * Request permission dengan user-friendly flow
     */
    async requestPermission() {
        if (!this.isInitialized) {
            await this.initialize();
        }

        if (isWebView) {
            console.log('[FIREBASE] Permission handled by native');
            return 'granted';
        }

        // Cek apakah sudah diizinkan
        if (this.permission === 'granted') {
            console.log('[FIREBASE] Permission sudah diberikan');
            return 'granted';
        }

        // Jika permission denied, beri tahu user cara enable manual
        if (this.permission === 'denied') {
            console.log('[FIREBASE] Permission ditolak oleh user');

            // Simpan state untuk ditampilkan di UI
            this.notifyListeners('permission_denied', {});
            return 'denied';
        }

        try {
            // Request permission dengan delay untuk UX
            await new Promise(resolve => setTimeout(resolve, 500));

            const permission = await Notification.requestPermission();
            this.permission = permission;

            console.log(`[FIREBASE] Permission status: ${permission}`);

            if (permission === 'granted') {
                this.notifyListeners('permission_granted', {});
            } else if (permission === 'denied') {
                this.notifyListeners('permission_denied', {});
            }

            return permission;

        } catch (error) {
            console.error('[FIREBASE] Permission request failed:', error);
            this.notifyListeners('error', error);
            return 'default';
        }
    }

    async comparePrevToken(currentToken) {
        if (!this.isInitialized) {
            const initialized = await this.initialize();
            if (!initialized) return null;
        }

        try {
            const oldToken = currentToken
            const registration = await this.registerServiceWorker();

            if (
                typeof this.vapidKey !== "string" ||
                !this.vapidKey.trim()
            ) {
                throw new Error(
                    "Firebase VAPID public key belum dikonfigurasi"
                );
            }

            const token = await getToken(this.messaging, {
                vapidKey: this.vapidKey,
                serviceWorkerRegistration: registration
            });

            if (!token) {
                console.warn("FCM: token tidak tersedia");
                return null;
            }

            // 🔥 COMPARE
            if (oldToken !== token) {
                console.log("🔥 TOKEN BERUBAH (WEB)");

                localStorage.setItem('fcm_token', token);

                // 🔥 WAJIB: kirim ke backend
                // await this.registerDevice(token);

                return {
                    token,
                    changed: true
                };
            }
            if (isDevelopment)
                console.log("✅ TOKEN SAMA (tidak berubah)");

            return {
                token,
                changed: false
            };

        } catch (error) {
            if (isDevelopment)
                console.error("[FCM] comparePrevToken gagal", {
                    name: error?.name,
                    message: error?.message,
                    code: error?.code,
                    stack: error?.stack,
                    origin: window.location.origin,
                    permission: Notification.permission,
                    vapidKeyConfigured: Boolean(this.vapidKey),
                });
            return null;
        }
    }

    /**
     * Get FCM Token dengan caching
     */

    async getToken() {
        const startedAt = Date.now();

        const log = (stage, details = {}) => {
            console.log(`[FIREBASE][TOKEN][${stage}]`, {
                elapsedMs: Date.now() - startedAt,
                ...details,
            });
        };

        try {
            log("START", {
                isInitialized: this.isInitialized,
                hasMessaging: Boolean(this.messaging),
                hasVapidKey: Boolean(this.vapidKey),
                hasCachedToken: Boolean(this.currentToken),
            });

            // 1. Pastikan Firebase sudah diinisialisasi.
            if (!this.isInitialized) {
                log("INITIALIZE_START");

                const initialized = await this.initialize();

                log("INITIALIZE_RESULT", {
                    initialized: Boolean(initialized),
                    isInitialized: this.isInitialized,
                    hasMessaging: Boolean(this.messaging),
                });

                if (!initialized || !this.isInitialized) {
                    log("ABORT_INITIALIZATION_FAILED");

                    return null;
                }
            }

            // 2. Gunakan cache internal jika tersedia.
            if (this.currentToken) {
                log("CACHED_TOKEN_FOUND", {
                    tokenLength: this.currentToken.length,
                });

                return this.currentToken;
            }

            // 3. Sinkronkan permission dengan kondisi browser.
            const browserPermission =
                typeof Notification !== "undefined"
                    ? Notification.permission
                    : "unsupported";

            log("PERMISSION_CHECK", {
                servicePermission: this.permission,
                browserPermission,
            });

            if (browserPermission !== "granted") {
                this.permission = browserPermission;

                log("ABORT_PERMISSION_NOT_GRANTED", {
                    browserPermission,
                });

                return null;
            }

            // Sinkronkan state internal service.
            this.permission = browserPermission;

            // 4. Pastikan Messaging tersedia.
            if (!this.messaging) {
                log("ABORT_MESSAGING_UNAVAILABLE");

                return null;
            }

            // 5. Pastikan VAPID key tersedia.
            if (
                typeof this.vapidKey !== "string" ||
                !this.vapidKey.trim()
            ) {
                log("ABORT_VAPID_KEY_MISSING");

                return null;
            }

            // 6. Daftarkan atau ambil service worker.
            log("SERVICE_WORKER_START");

            const registration =
                await this.registerServiceWorker();

            if (!registration) {
                log("ABORT_SERVICE_WORKER_UNAVAILABLE");

                return null;
            }

            log("SERVICE_WORKER_READY", {
                scope: registration.scope,
                active: Boolean(registration.active),
            });

            // 7. Minta token kepada Firebase.
            log("FCM_GET_TOKEN_START");

            const token = await getToken(this.messaging, {
                vapidKey: this.vapidKey,
                serviceWorkerRegistration: registration,
            });

            if (!token) {
                log("FCM_TOKEN_EMPTY", {
                    reason:
                        "Firebase getToken returned an empty token",
                });

                return null;
            }

            // 8. Simpan token hanya setelah berhasil diperoleh.
            this.currentToken = token;

            try {
                localStorage.setItem("fcm_token", token);
                localStorage.setItem(
                    "fcm_token_timestamp",
                    Date.now().toString()
                );
            } catch (storageError) {
                // Token tetap dapat digunakan pada sesi ini.
                // Kegagalan localStorage tidak membatalkan token.
                log("LOCAL_STORAGE_SAVE_FAILED", {
                    message: storageError?.message,
                });
            }

            this.notifyListeners("token_received", token);

            log("SUCCESS", {
                tokenLength: token.length,
                totalElapsedMs: Date.now() - startedAt,
            });

            return token;
        } catch (error) {
            log("FAILED", {
                name: error?.name,
                code: error?.code,
                message: error?.message,
                stack: error?.stack,
            });

            this.notifyListeners("error", error);

            return null;
        }
    }


    /**
     * Setup foreground message listener
     */
    setupForegroundListener() {
        if (!this.isInitialized) return;

        try {
            onMessage(this.messaging, (payload) => {
                console.log('[FIREBASE] Foreground message received:', payload);

                // Notify semua listeners
                this.notifyListeners('message', payload);

                // Play sound jika diperlukan
                this.playNotificationSound();
            });

            console.log('[FIREBASE] Foreground listener setup');

        } catch (error) {
            console.error('[FIREBASE] Setup foreground listener failed:', error);
        }
    }

    /**
     * Play notification sound
     */
    playNotificationSound() {
        try {
            const audio = new Audio('/notification.mp3');
            audio.volume = 0.3;
            audio.play().catch(e => console.warn('Audio play failed:', e));
        } catch (error) {
            console.warn('[FIREBASE] Notification sound failed:', error);
        }
    }

    /**
     * Clear token dan reset state
     */
    async clearToken() {
        try {
            if (this.currentToken) {
                if (!isWebView) /* jika adalah web app */
                    await deleteToken(this.messaging);
            }
        } catch (error) {
            console.warn('[FIREBASE] Clear token failed:', error);
        } finally {
            this.currentToken = null;
            localStorage.removeItem('fcm_token');
            localStorage.removeItem('fcm_token_android');
            localStorage.removeItem('fcm_token_timestamp');

            this.notifyListeners('token_cleared', {});
        }
    }

    /**
     * Check token validity dan refresh jika perlu
     */
    async checkTokenValidity() {
        const key_token = isWebView ? "fcm_token_android" : "fcm_token"
        const savedToken = localStorage.getItem(key_token);
        const savedTimestamp = localStorage.getItem('fcm_token_timestamp');

        if (!savedToken || !savedTimestamp) {
            return null;
        }


        const tokenAge = Date.now() - parseInt(savedTimestamp);
        const MAX_TOKEN_AGE = 7 * 24 * 60 * 60 * 1000; // 7 hari

        if (tokenAge > MAX_TOKEN_AGE) {
            console.log('[FIREBASE] Token expired, refreshing...');
            localStorage.removeItem('fcm_token');
            localStorage.removeItem('fcm_token_timestamp');
            return null;
        }

        // Use saved token
        this.currentToken = savedToken;
        return savedToken;
    }

    /**
     * Event listener system
     */
    addListener(event, callback) {
        const listener = { event, callback };
        this.listeners.add(listener);

        return () => {
            this.listeners.delete(listener);
        };
    }

    notifyListeners(event, data) {
        this.listeners.forEach(listener => {
            if (listener.event === event || listener.event === '*') {
                try {
                    listener.callback(data);
                } catch (error) {
                    console.error('[FIREBASE] Listener error:', error);
                }
            }
        });
    }

    /**
     * Get current state
     */
    getState() {
        return {
            isInitialized: this.isInitialized,
            permission: isWebView ? "granted" : this.permission,
            hasToken: !!this.currentToken,
            token: this.currentToken,
            isSupported: 'serviceWorker' in navigator && 'PushManager' in window
        };
    }
}

// Singleton instance
const firebaseService = new FirebaseService();
export default firebaseService;